import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import "./UnloadingPriorityVisibilityTable.css";
import Chevron from "../../../../../../assets/images/Chvron.svg";

const UnloadingPriorityGlobalGraph = ({ data, orderLossType }) => {
  const [expanded, setExpanded] = useState({});

  /* ✅ FORMATTER (K / L / Cr) */
  const formatToCappedUnit = (value) => {
    if (value === undefined || value === null) return "-";
    if (value < 1000) {
      return `${value}`;
    }
    if (value < 100000) {
      return `${(value / 1000).toFixed(1)} K`;
    }

    if (value < 10000000) {
      return `${(value / 100000).toFixed(1)} L`;
    }

    return `${(value / 10000000).toFixed(1)} Cr`;
  };

  /* ✅ GROUPING LOGIC */
  const groupByReceivingPlant = (data = []) => {
    return data.reduce((acc, item) => {
      const plant = item["Receiving Plant"];

      if (!acc[plant]) {
        acc[plant] = {
          totalRow: null,
          rows: [],
        };
      }

      if (item.is_receiving_total === 1) {
        acc[plant].totalRow = item;
      } else {
        acc[plant].rows.push(item);
      }

      return acc;
    }, {});
  };

  const groupedData = useMemo(
    () => groupByReceivingPlant(data),
    [data]
  );

  const toggleExpand = (plant) => {
    setExpanded((prev) => ({
      ...prev,
      [plant]: !prev[plant],
    }));
  };

  return (
    <div className="tableWrapper">
      <table className="visibilityTable">
        <thead>
          <tr>
            <th className="iconCol"></th>
            <th>Receiving Plant</th>
            <th>Total Shipment</th>
            <th>No of CLDS Shipment</th>
            <th>{orderLossType} Mitigated Value</th>
            <th>Value of Shipment</th>
            <th>Median Waiting Time</th>
          </tr>
        </thead>

        <tbody>
          {Object.entries(groupedData).map(([plant, plantData]) => {
            const total = plantData.totalRow;

            return (
              <React.Fragment key={plant}>
                {/* ✅ Parent Row */}
                <tr className="parentRow">
                  <td className="iconCol">
                    <button
                      className="expandBtn"
                      onClick={() => toggleExpand(plant)}
                    >
                      {expanded[plant] ? (
                        <img
                          src={Chevron}
                          className="chevronImg"
                          style={{ transform: "rotate(90deg)" }}
                          alt="Chevron"
                        />
                      ) : (
                        <img
                          src={Chevron}
                          className="chevronImg"
                          alt="Chevron"
                        />
                      )}
                    </button>
                  </td>

                  <td>{plant}</td>

                  <td>{total?.["Total Shipment"] ?? "-"}</td>

                  <td title={total?.["No of CLDS Shipment"]}>
                    {formatToCappedUnit(
                      total?.["No of CLDS Shipment"]
                    )}
                  </td>

                  {/* ✅ Mitigated Value (formatted) */}
                  <td
                    title={total?.[`${orderLossType} Mitigated Value`]}
                  >
                    {formatToCappedUnit(
                      total?.[`${orderLossType} Mitigated Value`]
                    )}
                  </td>

                  {/* ✅ Value of Shipment (formatted) */}
                  <td title={total?.["Value of Shipment"]}>
                    {formatToCappedUnit(
                      total?.["Value of Shipment"]
                    )}
                  </td>

                  <td>{total?.["Median of Waiting Time"]}</td>
                </tr>

                {/* ✅ Child Rows */}
                {expanded[plant] &&
                  plantData.rows.map((row, idx) => (
                    <tr className="childRow" key={`unload-child-${plant}-${idx}-${row["Sending Plant"]}`}>
                      <td className="iconCol"></td>

                      <td className="indentCell">
                        {row["Sending Plant"]}
                      </td>

                      <td>{row["Total Shipment"]}</td>

                      <td>
                        {row["No of CLDS Shipment"] ?? "-"}
                      </td>

                      {/* ✅ Mitigated Value */}
                      <td
                        title={row[`${orderLossType} Mitigated Value`]}
                      >
                        {formatToCappedUnit(
                          row[`${orderLossType} Mitigated Value`]
                        )}
                      </td>

                      {/* ✅ Value of Shipment */}
                      <td title={row["Value of Shipment"]}>
                        {formatToCappedUnit(
                          row["Value of Shipment"]
                        )}
                      </td>

                      <td>{row["Median of Waiting Time"]}</td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

UnloadingPriorityGlobalGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  orderLossType: PropTypes.string,
};

export default UnloadingPriorityGlobalGraph;
